import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import {
  ApiKeyScope,
  ApiOrganization,
  ApiOrganizationApiKey,
  createApiKey,
  fetchOrganizationApiKeys,
  revokeApiKey,
  updateApiKeyLabel,
} from 'src/lib/api/auth';

export default function useOrganizationApiKeys(
  organizationId: ApiOrganization['id']
): UseQueryResult<ApiOrganizationApiKey[]> {
  return useQuery(['organization', organizationId, 'apiKeys'], () =>
    fetchOrganizationApiKeys(organizationId)
  );
}

type CreateApiKeyArgs = {scope: ApiKeyScope; expiresAt?: string | null; label?: string | null};

export function useCreateApiKey(
  organizationId: ApiOrganization['id']
): UseMutationResult<ApiOrganizationApiKey, Error, CreateApiKeyArgs> {
  const queryClient = useQueryClient();

  return useMutation(
    ({scope, expiresAt, label}: CreateApiKeyArgs) =>
      createApiKey(organizationId, scope, expiresAt, label),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization', organizationId, 'apiKeys']);
      },
    }
  );
}

type UpdateApiKeyLabelArgs = {apiKey: string; label: string | null};

export function useUpdateApiKeyLabel(
  organizationId: ApiOrganization['id']
): UseMutationResult<ApiOrganizationApiKey, Error, UpdateApiKeyLabelArgs> {
  const queryClient = useQueryClient();

  return useMutation(
    ({apiKey, label}: UpdateApiKeyLabelArgs) => updateApiKeyLabel(organizationId, apiKey, label),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization', organizationId, 'apiKeys']);
      },
    }
  );
}

export function useRevokeApiKey(
  organizationId: ApiOrganization['id']
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation((apiKey: string) => revokeApiKey(organizationId, apiKey), {
    onSuccess: () => {
      queryClient.invalidateQueries(['organization', organizationId, 'apiKeys']);
    },
  });
}
